/**
 * ChatHeader — SideChat header with position controls, collapse, fullscreen, export.
 * Rule 94: Modüler — header ayrı component.
 */
import { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Download, Maximize2, Minimize2, Zap } from 'lucide-react';
import type { SideChatPosition } from './useSideChatPosition';
import { AgentSwitcher, type AgentMode } from './AgentSwitcher';

const POSITIONS: { label: string; value: SideChatPosition; icon: React.ReactNode }[] = [
  { label: 'Right',  value: 'right',  icon: <ChevronRight size={14} /> },
  { label: 'Left',   value: 'left',   icon: <ChevronLeft size={14} /> },
  { label: 'Bottom', value: 'bottom', icon: <ChevronDown size={14} /> },
]

interface Props {
  position: SideChatPosition
  setPos: (p: SideChatPosition) => void
  collapsed: boolean
  setCollapsed: (v: boolean | ((prev: boolean) => boolean)) => void
  isFullscreen: boolean
  toggleFullscreen: () => void
  onExport?: () => void
  showSkills?: boolean
  onSkillsToggle?: () => void
}

export default function ChatHeader({ position, setPos, collapsed, setCollapsed, isFullscreen, toggleFullscreen, onExport, showSkills, onSkillsToggle }: Props) {
  const [agentMode, setAgentMode] = useState<AgentMode>(
    () => (localStorage.getItem('runai_chat_agent_mode') as AgentMode) || 'general'
  )

  function handleModeChange(mode: AgentMode) {
    setAgentMode(mode)
    localStorage.setItem('runai_chat_agent_mode', mode)
    window.dispatchEvent(new CustomEvent('runai:agent-mode-change', { detail: { mode } }))
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      borderBottom: collapsed ? 'none' : '1px solid var(--os-border)',
      flexShrink: 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between',
        padding: '6px 10px',
      }}>
        {!collapsed && (
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--os-accent)' }}>Chat</div>
        )}
        <div style={{ display: 'flex', gap: 4 }}>
          <HeaderButton onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed
              ? (position === 'left' ? <ChevronRight size={14} /> : <ChevronLeft size={14} />)
              : (position === 'left' ? <ChevronLeft size={14} /> : <ChevronRight size={14} />)
            }
          </HeaderButton>
          {!collapsed && (
            <>
              <HeaderButton onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} active={isFullscreen}>
                {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              </HeaderButton>
              {onExport && (
                <HeaderButton onClick={onExport} title="Export chat history">
                  <Download size={13} />
                </HeaderButton>
              )}
              {onSkillsToggle && (
                <HeaderButton onClick={onSkillsToggle} title="Skills" active={showSkills}>
                  <Zap size={13} />
                </HeaderButton>
              )}
              {!isFullscreen && POSITIONS.map(p => (
                <HeaderButton key={p.value} onClick={() => setPos(p.value)} title={p.label} active={position === p.value}>
                  {p.icon}
                </HeaderButton>
              ))}
            </>
          )}
        </div>
      </div>
      {!collapsed && (
        <AgentSwitcher mode={agentMode} onChange={handleModeChange} />
      )}
    </div>
  )
}

function HeaderButton({ onClick, title, active, children }: {
  onClick: () => void; title: string; active?: boolean; children: React.ReactNode
}) {
  return (
    <button onClick={onClick} title={title}
      style={{
        width: 22, height: 22, borderRadius: 6,
        background: active ? 'rgba(99,102,241,0.2)' : 'transparent',
        border: 'none', cursor: 'pointer',
        color: active ? '#818cf8' : 'var(--text-secondary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      {children}
    </button>
  )
}
